import { useEffect } from 'react'
import { defaults, site } from '../config/seo'
import { useProducts } from '../context/ProductsContext'

// ── DOM helpers ──────────────────────────────────────────────

function setTitle(title, titleRaw, suffix) {
  document.title = titleRaw ? title : `${title}${suffix}`
}

function upsertMeta(attr, attrValue, content) {
  if (!content) {
    // Remove the tag if it exists but content was cleared
    document.querySelector(`meta[${attr}="${attrValue}"]`)?.remove()
    return
  }
  let el = document.querySelector(`meta[${attr}="${attrValue}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, attrValue)
    document.head.appendChild(el)
  }
  el.setAttribute('content', String(content))
}

function upsertCanonical(href) {
  if (!href) return
  let el = document.querySelector('link[rel="canonical"]')
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function upsertJsonLd(data) {
  let el = document.querySelector('script[type="application/ld+json"][data-lookslab-seo]')
  if (!el) {
    el = document.createElement('script')
    el.setAttribute('type', 'application/ld+json')
    el.setAttribute('data-lookslab-seo', 'true')
    document.head.appendChild(el)
  }
  el.textContent = JSON.stringify(data, null, 0)
}

function removeJsonLd() {
  document.querySelector('script[type="application/ld+json"][data-lookslab-seo]')?.remove()
}

// ── Deep merge ───────────────────────────────────────────────

function merge(base, override) {
  if (!override) return base
  const out = { ...base, ...override }
  if (base.og      || override.og)      out.og      = { ...(base.og      ?? {}), ...(override.og      ?? {}) }
  if (base.twitter || override.twitter) out.twitter  = { ...(base.twitter ?? {}), ...(override.twitter ?? {}) }
  return out
}

// ── Component ────────────────────────────────────────────────

/**
 * <SEO config={pages.home} />  — full control (title/description/robots/canonical/og/twitter/jsonLd)
 * <SEO title="..." description="..." />  — convenience form for simple pages
 *
 * Merges: static defaults → dynamic global settings (admin catalog_settings.seo) → page-specific config
 */
export default function SEO({ config, title, description }) {
  const { seoSettings, branding } = useProducts()
  const pageConfig = config ?? { title, description }

  useEffect(() => {
    // Dynamic site settings override the static config file
    const dynSite = { ...site, ...(seoSettings?.site ?? {}) }
    const dynOg   = seoSettings?.og ?? {}

    // Build effective defaults: static → dynamic global overrides
    const effectiveDefaults = merge(
      {
        ...defaults,
        og: {
          ...defaults.og,
          image:       dynOg.defaultOgImage     ?? defaults.og?.image,
          imageAlt:    dynOg.defaultOgImageAlt   ?? defaults.og?.imageAlt,
          imageWidth:  dynOg.defaultOgImageWidth  ?? defaults.og?.imageWidth,
          imageHeight: dynOg.defaultOgImageHeight ?? defaults.og?.imageHeight,
          siteName:    dynSite.name,
          locale:      dynSite.locale,
        },
        twitter: {
          ...defaults.twitter,
          site: dynSite.twitterHandle,
        },
      },
      seoSettings?.defaults ?? {},
    )

    const c = merge(effectiveDefaults, pageConfig)
    const suffix = dynSite.titleSuffix ?? site.titleSuffix

    // Title
    setTitle(c.title ?? effectiveDefaults.title, c.titleRaw ?? false, suffix)

    // Core meta
    upsertMeta('name', 'description', c.description)
    upsertMeta('name', 'keywords',    c.keywords)
    upsertMeta('name', 'robots',      c.robots)
    upsertMeta('name', 'author',      c.author ?? dynSite.name)

    // Canonical
    upsertCanonical(c.canonical)

    // Open Graph
    const og = c.og ?? {}
    upsertMeta('property', 'og:type',         og.type)
    upsertMeta('property', 'og:title',        og.title        ?? c.title)
    upsertMeta('property', 'og:description',  og.description  ?? c.description)
    upsertMeta('property', 'og:url',          og.url          ?? c.canonical)
    upsertMeta('property', 'og:image',        og.image)
    upsertMeta('property', 'og:image:alt',    og.imageAlt)
    upsertMeta('property', 'og:image:width',  og.imageWidth)
    upsertMeta('property', 'og:image:height', og.imageHeight)
    upsertMeta('property', 'og:site_name',    og.siteName     ?? dynSite.name)
    upsertMeta('property', 'og:locale',       og.locale       ?? dynSite.locale)

    // Twitter
    const tw = c.twitter ?? {}
    upsertMeta('name', 'twitter:card',        tw.card)
    upsertMeta('name', 'twitter:site',        tw.site         ?? dynSite.twitterHandle)
    upsertMeta('name', 'twitter:creator',     tw.creator)
    upsertMeta('name', 'twitter:title',       tw.title        ?? og.title ?? c.title)
    upsertMeta('name', 'twitter:description', tw.description  ?? og.description ?? c.description)
    upsertMeta('name', 'twitter:image',       tw.image        ?? og.image)
    upsertMeta('name', 'twitter:image:alt',   tw.imageAlt     ?? og.imageAlt)

    // Webmaster verification tags
    const crawlers = seoSettings?.crawlers ?? {}
    upsertMeta('name', 'google-site-verification', crawlers.googleVerification || null)
    upsertMeta('name', 'msvalidate.01',            crawlers.bingVerification   || null)
    upsertMeta('name', 'yandex-verification',      crawlers.yandexVerification || null)

    // JSON-LD — merge dynamic organization data into the home page schema
    if (c.jsonLd) {
      const dynOrg = seoSettings?.jsonLd ?? {}
      const jsonLd = {
        ...c.jsonLd,
        ...(c.jsonLd['@type'] === 'Organization' ? {
          logo: branding?.logoDark?.url ?? c.jsonLd.logo,
        } : {}),
        ...(c.jsonLd['@type'] === 'Organization' && dynOrg.organizationName ? {
          name:        dynOrg.organizationName,
          url:         dynOrg.organizationUrl  ?? c.jsonLd.url,
          description: dynOrg.organizationDescription ?? c.jsonLd.description,
          sameAs:      dynOrg.sameAs?.length ? dynOrg.sameAs : (c.jsonLd.sameAs ?? []),
        } : {}),
      }
      upsertJsonLd(jsonLd)
    } else {
      removeJsonLd()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(pageConfig), seoSettings, branding])

  return null
}
